import { FileCheck2, Plus } from "lucide-react";
import { useNotify, useTranslate, useUpdate, type Identifier } from "ra-core";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";

import { LoadMore } from "../../operations/LoadMore";
import { useInfiniteRecords } from "../../operations/useInfiniteRecords";
import { getChargeStatus } from "../../providers/commons/financeSchedule";
import type { FinanceCharge } from "../../types";
import { FinanceListToolbar } from "../FinanceListToolbar";
import { CHARGE_VIEWS, getChargeViewFilter, parseView } from "../financeViews";
import { GenerateMonthButton } from "../GenerateMonthButton";
import { useCompanyNames } from "../useCompanyNames";
import { useFinanceErrorHandler } from "../useFinanceErrorHandler";
import { ChargeDialog } from "./ChargeDialog";
import { ChargeRows } from "./ChargeRows";
import { PaymentDialog } from "./PaymentDialog";

export interface ChargeActions {
  onOpen: (charge: FinanceCharge) => void;
  onMarkSent: (charge: FinanceCharge) => void;
  onRegisterPayment: (charge: FinanceCharge) => void;
}

/** Receivables: quick filters, server pagination, send / payment actions */
export const ChargeList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const onError = useFinanceErrorHandler();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const view = parseView(searchParams.get("view"), CHARGE_VIEWS);
  const q = searchParams.get("q") ?? "";
  const filter = useMemo(
    () => ({
      ...getChargeViewFilter(view, new Date()),
      ...(q ? { q } : {}),
    }),
    [view, q],
  );
  const sort = useMemo(
    () => ({
      field: "due_date",
      order: view === "paid" || view === "all" ? "DESC" : "ASC",
    }),
    [view],
  ) as { field: string; order: "ASC" | "DESC" };
  const {
    records: charges,
    total,
    isPending,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteRecords<FinanceCharge>("finance_charges", filter, sort);
  const companyNames = useCompanyNames(charges);
  const [editing, setEditing] = useState<FinanceCharge | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [paying, setPaying] = useState<FinanceCharge | null>(null);
  const [update] = useUpdate();

  const actions: ChargeActions = {
    onOpen: setEditing,
    onRegisterPayment: setPaying,
    onMarkSent: (charge) =>
      update(
        "finance_charges",
        {
          id: charge.id,
          data: { sent_at: new Date().toISOString() },
          previousData: charge,
        },
        { onSuccess: () => notify("crm.finance.charges.marked_sent"), onError },
      ),
  };

  const today = new Date();
  const rows = charges.map((charge) => ({
    charge,
    status: getChargeStatus(charge, today),
    companyName: companyNames.get(charge.company_id as Identifier) ?? "",
  }));

  return (
    <div className="flex flex-col gap-3">
      <FinanceListToolbar
        views={CHARGE_VIEWS}
        view={view}
        labelPrefix="crm.finance.charges.views"
        actions={
          <>
            <GenerateMonthButton />
            <Button
              type="button"
              onClick={() => setIsCreating(true)}
              aria-label={translate("crm.finance.charges.create")}
            >
              <Plus />
              <span className="hidden md:inline">
                {translate("crm.finance.charges.create")}
              </span>
            </Button>
          </>
        }
      />
      {isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : total === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground">
          <FileCheck2 className="size-8" />
          {translate(
            view === "all" && !q
              ? "crm.finance.charges.empty"
              : "crm.finance.empty_filtered",
          )}
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {translate("crm.finance.charges.count", { smart_count: total })}
          </p>
          <ChargeRows rows={rows} actions={actions} isMobile={isMobile} />
          <LoadMore
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
          />
        </>
      )}
      <ChargeDialog
        open={isCreating || editing != null}
        charge={editing}
        onClose={() => {
          setIsCreating(false);
          setEditing(null);
        }}
      />
      <PaymentDialog charge={paying} onClose={() => setPaying(null)} />
    </div>
  );
};
